/**
 * ============================================================
 * FILE: ui/preact/dialog/DialogUI.js – NPC-Dialoge (Preact)
 * ============================================================
 */

import { h, html, useStateSelector, useEventBus, useState, useEffect } from '../setup.js';
import { EVENTS } from '../../../core/events/definitions.js';
import { getNPC, getDialog } from '../../../data/dialogs.js';
import { getCinematicScene } from '../../../data/cinematic_scenes.js';
import { logger } from '../../../core/logger.js';

export function DialogUI({ stateManager, eventBus, services }) {
  const { storyBranchService, codexService, resourceService, i18nService } = services;
  const [isOpen, setIsOpen] = useState(false);
  const [npcId, setNpcId] = useState(null);
  const [dialogId, setDialogId] = useState(null);
  const [history, setHistory] = useState([]);
  const [exiting, setExiting] = useState(false);

  const [lang, setLang] = useState(i18nService.getLanguage());
  useEventBus(eventBus, 'i18n:languageChanged', (newLang) => {
    setLang(newLang);
  });

  const getLocText = (obj, prop = 'text') => {
    if (!obj) return '';
    if (lang === 'en' && obj[prop + '_en']) {
      return obj[prop + '_en'];
    }
    return obj[prop] || '';
  };

  const flags = useStateSelector(stateManager, (state) => state.storyBranch.flags);

  useEventBus(eventBus, EVENTS.UI_OPEN_DIALOG, (data) => {
    if (data && data.npcId) {
      setExiting(false);
      setNpcId(data.npcId);
      setDialogId(null);
      setHistory([]);
      const npc = getNPC(data.npcId);
      if (npc) {
        setDialogId(npc.defaultDialog || npc.dialogs[0]?.id || null);
      }
      setIsOpen(true);
    }
  });

  const closeDialog = () => {
    if (exiting) return;
    setExiting(true);
    setTimeout(() => {
      setIsOpen(false);
      setExiting(false);
    }, 450);
  };

  if (!isOpen || !npcId) return null;

  const npc = getNPC(npcId);
  if (!npc) return null;

  let dialog = dialogId ? getDialog(npcId, dialogId) : null;
  if (!dialog && npc.dialogs.length > 0) {
    dialog = npc.dialogs[0];
    setDialogId(dialog.id);
  }
  if (!dialog) return null;

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (dialog && (dialog.isEnding || dialog.options.length === 0 || dialog.canSkip !== false)) {
          closeDialog();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, dialog, exiting]);

  const sceneId = dialog.cinematic || npc.cinematic || 'archive-halls';
  const scene = getCinematicScene(sceneId);
  const isCinematic = npc.isCinematic || dialog.isCinematic || !!dialog.cinematic || !!npc.cinematic ||
    ['theron', 'nyx', 'prestige_guide', 'shadow_figure', 'archivist', 'merchant', 'guardian_npc', 'shadow_voice'].includes(npcId);

  const handleOption = (option) => {
    if (option.action) {
      switch (option.action) {
        case 'trade_particles':
          const state = stateManager.getState();
          if (BigInt(state.resources.particles || '0') >= BigInt(100)) {
            resourceService.removeParticles(100);
            resourceService.addRelics(10);
            eventBus.publish('ui:showToast', {
              message: lang === 'de' ? '⚗️ 100 Partikel gegen 10 Relikte getauscht!' : '⚗️ Traded 100 Particles for 10 Relics!',
              type: 'success',
              duration: 2000
            });
          } else {
            eventBus.publish('ui:showToast', {
              message: lang === 'de' ? '❌ Nicht genug Partikel für den Tausch.' : '❌ Not enough Particles for the trade.',
              type: 'warning',
              duration: 2000
            });
          }
          break;
        default:
          logger.warn('[Dialog] Unbekannte Aktion:', option.action);
      }
    }

    const nextDialogId = option.next;
    if (nextDialogId) {
      const nextDialog = getDialog(npcId, nextDialogId);
      if (nextDialog) {
        setHistory([...history, { from: dialogId, option: getLocText(option, 'text'), to: nextDialogId }]);
        setDialogId(nextDialogId);
        if (codexService && codexService.unlockFromNPC) {
          codexService.unlockFromNPC(npcId);
        }
        return;
      }
    }

    closeDialog();
  };

  if (isCinematic) {
    const glow = scene.glowColor || 'var(--color-primary)';
    const bgStyle = scene.background || 'rgba(5,5,7,0.95)';
    const ambientClass = scene.ambientClass || '';
    const vignetteClass = scene.vignette === 'extreme' ? 'cinematic-vignette-extreme' :
                          scene.vignette === 'heavy' ? 'cinematic-vignette-heavy' :
                          scene.vignette === 'medium' ? 'cinematic-vignette-medium' : '';
    const exitClass = exiting ? 'cin-exit' : '';

    return html`
      <div class="cinematic-overlay cinematic-bars cinematic-active ${ambientClass} ${vignetteClass} ${exitClass}"
           style="background: ${bgStyle};"
           onClick=${(e) => { if (e.target === e.currentTarget && dialog.canSkip !== false) closeDialog(); }}>
        <div class="cin-particles" aria-hidden="true"></div>
        <div class="cinematic-content" style="max-width: 800px; width: 90%; text-align: center;">
          <div class="cin-portrait" style="font-size: 5.5rem; margin-bottom: 1.5rem; filter: drop-shadow(0 0 25px ${glow});">
            ${npc.portrait || '👤'}
          </div>
          <div class="cin-scene-label" style="font-size: 0.7rem; color: ${glow}; opacity: 0.65; letter-spacing: 3px; text-transform: uppercase; margin-bottom: 0.6rem; font-family: var(--font-header, Cinzel, serif);">
            ${lang === 'en' && scene.name_en ? scene.name_en : scene.name}
          </div>
          <h2 class="cinzel cin-title" style="color: ${glow}; font-size: 2.2rem; letter-spacing: 3px; text-shadow: 0 0 20px ${glow}; margin-bottom: 2rem; text-transform: uppercase;">
            ${getLocText(npc, 'name')}
          </h2>
          <div class="cin-text" style="font-family: 'Outfit', sans-serif; font-size: 1.35rem; color: #fff; line-height: 1.8; margin-bottom: 3.5rem; text-shadow: 0 2px 8px rgba(0,0,0,0.9); font-style: ${scene.textStyle || 'italic'}; background: rgba(0,0,0,0.35); padding: 1.5rem; border-radius: 8px; border-left: 2px solid ${glow}; border-right: 2px solid ${glow};">
            "${getLocText(dialog, 'text')}"
          </div>
          <div class="dialog-options cin-options" style="display: flex; flex-direction: column; gap: 1rem; align-items: center;">
            ${dialog.isEnding || dialog.options.length === 0 ? html`
              <button class="glass-btn primary cinzel" style="padding: 1rem 4rem; font-size: 1.2rem; border-color: ${glow}; letter-spacing: 2px;" onClick=${() => closeDialog()}>
                ${lang === 'de' ? 'Fortfahren' : 'Continue'}
              </button>
            ` : dialog.options.map(opt => html`
              <button class="glass-btn" style="width: 100%; max-width: 550px; padding: 1.2rem 2rem; font-size: 1.1rem; background: rgba(0,0,0,0.6); border: 1px solid rgba(255,255,255,0.15); transition: all 0.3s ease; text-align: center; justify-content: center;"
                      onMouseOver=${(e) => { e.currentTarget.style.borderColor = glow; e.currentTarget.style.boxShadow = `0 0 15px ${glow}`; }}
                      onMouseOut=${(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; e.currentTarget.style.boxShadow = 'none'; }}
                      onClick=${() => handleOption(opt)}>
                ${getLocText(opt, 'text')}
              </button>
            `)}
          </div>
        </div>
      </div>
    `;
  }

  return html`
    <div class="modal-overlay" style="display: flex;" onClick=${(e) => { if (e.target === e.currentTarget && dialog.canSkip !== false) setIsOpen(false); }}>
      <div class="modal-content glass-panel" style="width: 600px; max-width: 95vw; max-height: 80vh;" onClick=${(e) => e.stopPropagation()}>
        <button class="modal-close" onClick=${() => setIsOpen(false)}>×</button>

        <div class="dialog-npc-header" style="display: flex; align-items: center; gap: 1rem; margin-bottom: 0.8rem; padding-bottom: 0.5rem; border-bottom: 1px solid rgba(197,160,89,0.1);">
          <span class="dialog-npc-portrait" style="font-size: 2.8rem; filter: drop-shadow(0 0 10px var(--color-primary-glow));">${npc.portrait || '👤'}</span>
          <div class="dialog-npc-info" style="flex: 1;">
            <div class="dialog-npc-name" style="font-family: var(--font-header); font-size: 1.2rem; color: var(--color-primary);">${getLocText(npc, 'name')}</div>
            <div class="dialog-npc-title" style="color: var(--color-text-muted); font-size: 0.85rem;">${getLocText(npc, 'title')}</div>
          </div>
        </div>

        <div class="dialog-text-content" style="padding: 1rem 1.2rem; min-height: 80px; line-height: 1.8; background: rgba(0,0,0,0.25); border-left: 3px solid var(--color-primary); border-radius: 2px;">
          ${getLocText(dialog, 'text')}
        </div>

        <div class="dialog-options" style="margin-top: 1rem;">
          ${dialog.isEnding || dialog.options.length === 0 ? html`
            <button class="glass-btn primary dialog-close-btn" style="width: 100%; padding: 1rem; font-size: 1.1rem; margin-top: 0.5rem;" onClick=${() => setIsOpen(false)}>✕ ${lang === 'de' ? 'Schließen' : 'Close'}</button>
          ` : dialog.options.map(opt => html`
            <button class="glass-btn dialog-option-btn" style="width: 100%; padding: 0.8rem 1.2rem; margin-bottom: 0.5rem; justify-content: flex-start; text-align: left; background: rgba(0,0,0,0.2); border-left: 2px solid var(--color-text-muted); transition: all 0.3s ease;"
                    onMouseOver=${(e) => { e.currentTarget.style.borderColor = 'var(--color-primary)'; }}
                    onMouseOut=${(e) => { e.currentTarget.style.borderColor = 'var(--color-text-muted)'; }}
                    onClick=${() => handleOption(opt)}>
              ${getLocText(opt, 'text')}
            </button>
          `)}
        </div>
      </div>
    </div>
  `;
}
